<template>
    <b-overlay :show="loading">
        <template #overlay>
            <div class="text-center">
                <b-spinner label="Loading..."></b-spinner>
                <div class="mt-2 mb-2">
                    <template v-if="apply_loading">
                        {{ $t('평가를 적용 중입니다...') }}    
                    </template>
                    <template v-if="aiEvDataFetchLoading || fetch_loading">
                        {{ $t('AI 평가 데이터를 불러오는 중입니다...') }}
                    </template>
                </div>
            </div>
        </template>
        <div class="ai-ev-container">
            <div class="ai-ev-actions">
                <div class="versions">
                    <div class="label">
                        {{ $t('평가 버전') }}
                    </div>
                    <div 
                         v-b-tooltip.hover
                         class="ai-refresh-btn"
                         :title="$t('현재 문항의 AI 평가 데이터 \n새로 불러오기')"
                         style="margin-left: 4px; margin-right: 2px"
                         @click="fetchSubAiEvData"
                    >
                        <fa icon="sync-alt"></fa>
                    </div>
                    <b-form-select v-model="current_ai_ev_version" class="version-select"
                                   size="sm"
                    >
                        <b-form-select-option v-if="no_ai_audit"
                                              disabled
                                                :value="null"
                        >
                            {{ $t('No Data.')}}
                        </b-form-select-option>
                        <b-form-select-option v-for="(data) in arr_ai_ev_data"
                                              :key="`ai-ev-version-${data.id}`"
                                              :value="data.id">
                            {{ data.version }}
                        </b-form-select-option>
                    </b-form-select>
                    <div>
                        <b-button size="sm" variant="dark-border"
                                  :disabled="loading || no_ai_audit"
                                  @click="openDetailAIModal"
                        >
                            상세
                        </b-button>
                    </div>
                </div>

                <div class="d-flex">
                    <b-button size="sm" class="mr-2"
                              :disabled="loading || !can_request_ai_assessment"
                              @click="requestAIAssessment"
                    >
                        {{ $t('AI 평가 요청') }}
                    </b-button>
                    <b-button size="sm" variant="primary"
                              :disabled="loading || current_ai_ev_data?.status !== 'completed'"
                              @click="copySub"
                    >
                        {{ $t('문항 AI 평가 적용') }}
                    </b-button>
                </div>


            </div>
            <b-row no-gutters class="mx-0 border-bottom border-color-gray-300 pb-1 mb-2 gray-550">
                <b-col class="col-9 f-90"> {{ $t('평가기준') }}</b-col>
                <b-col class="col-2 text-center f-90"> 점수</b-col>
                <b-col class="col-1 text-center f-90"> 항목적용</b-col>
            </b-row>
            <b-row v-for="(item) in ev_scoring_list" :key="`subs-${sub_code}-ev-${item.ev_id}`"
                   no-gutters
                   :class="['d-flex justify-content-bewteen align-items-center mx-0 mb-3',check_diff_ev(item)]"
            >
                <b-col class="col-9" style="margin-top: 12px">
                    <b-form-checkbox v-model="get_ai_ev_item(item.ev_id).is_satisfied"
                                     class="f-95"
                                     style="margin-left: 12px"
                                     disabled
                    >
                        {{ item.ev_name }}
                    </b-form-checkbox>
                </b-col>
                <b-col cols="2" :class="['text-center']">
                <span>
                    {{ get_ai_ev_item(item.ev_id).is_satisfied ? item.ev_score : 0 }}
                </span>
                </b-col>
                <b-col class="col-1 text-center">
                    <b-button class="py-1" variant="icon-dark"
                              :disabled="loading || current_ai_ev_data?.status !== 'completed'"
                              @click="copyItem(item)"
                    >
                        <fa icon="file-import"></fa>
                    </b-button>
                </b-col>
                <b-col class="col-12 d-flex" style="margin-bottom: 12px">
                    <template v-if="get_ai_ev_item(item.ev_id).converted_reason">
                        <div class="audit-container">
                            <b-form-select
                                v-model="get_ai_ev_item(item.ev_id).converted_reason.audit_category_code"
                                class="audit-category"
                                disabled
                            >
                                <b-form-select-option v-for="category in audit_category"
                                                      :key="category.code"
                                                      :value="category.code"
                                                      :disabled="category.code == null"
                                >
                                    {{ get_audit_category_name(category, $i18n) }}
                                </b-form-select-option>
                            </b-form-select>
                            <b-form-textarea
                                v-model="get_ai_ev_item(item.ev_id).converted_reason.audit_comment"
                                placeholder="평가 항목별 의견"
                                :class="[{ 'blue7 italic': item.is_audited },'f-95 audit-comment', 'cursor']"
                                readonly
                                :rows="6"
                                :max-rows="6"
                                @click="copyAuditBasis(get_ai_ev_item(item.ev_id).converted_reason.audit_comment)"
                            >
                            </b-form-textarea>
                        </div>
                    </template>
                    <div v-else class="no-ai-audit-data-container">
                        <div class="no-ai-audit_title-wrapper">
                            <fa icon="robot" class="blue7 ai-report-icon" style="margin-right: 6px"/>
                            <div class="no-ai-audit_title">
                                {{ no_ai_audit_title(item.ev_id) }}
                            </div>
                        </div>
                        <div class="no-ai-audit_desc">{{ no_ai_audit_desc(item.ev_id) }}</div>
                    </div>
                    
                </b-col>
            </b-row>
        </div>
        
    </b-overlay>
</template>

<script>
import {mapActions, mapGetters, mapMutations, mapState} from 'vuex';
import dayjs from 'dayjs';
import {aiAuditLoadingText, checkCanRequestAi} from '@shared/components/admin/pages/Management/AIAssessment/data';
import {getDate} from '@shared/components/admin/pages/Management/common-data';

export default {
    name: 'AIEvaluationComponent',
    props: {
        sub_code: {
            type: String
        },
        sub_id: {
            type: [Number,null],
            default: null
        },
        sub_label: {
            type: [String,null],
            default: null
        },
        ev_scoring_list: {
            type: Array,
            default: () => []
        },
    },
    data() {
        return {
            current_ai_ev_version: null,
            version_list: [],
            detail_ai_assessment_data: null,
            fetch_loading: false,
            apply_loading: false,
            
        };
    },
    computed: {
        ...mapState('evaluations', ['audit_category']),
        ...mapState('ai_assessment', ['aiEvData', 'aiEvDataFetchLoading']),
        ...mapGetters('evaluations', ['get_audit_category_name']),
        get_date() {
            return (date) => { return getDate(date); };
        },
        sub_ai_ev_data(){
            return this.aiEvData?.[this.sub_id] || null;
        },
        no_ai_audit(){
            return this.sub_ai_ev_data === null;
        },
        can_request_ai_assessment(){
            const lastest_status = this.arr_ai_ev_data?.[0]?.status ?? null;
            const last_requested_at = this.arr_ai_ev_data?.[0]?.updated_at ?? this.arr_ai_ev_data?.[0]?.created_at ?? undefined;
            
            return checkCanRequestAi(lastest_status, last_requested_at) || this.no_ai_audit;
        },
        arr_ai_ev_data() {
            return Object.values(this.sub_ai_ev_data || {}).sort((a, b) => {
                return dayjs(b.created_at).diff(dayjs(a.created_at));
            });
        },
        current_ai_ev_data(){
            return this.sub_ai_ev_data?.[this.current_ai_ev_version] ?? null;
        },
        check_diff_ev(){
            return (ev_item) => {
                if(this.current_ai_ev_data?.status !== 'completed') return {};
                return {
                    'ev-diff-bg': this.current_ai_ev_data?.valid_map?.[ev_item.ev_id]?.is_satisfied !== ev_item.manual_satisfied,
                    'ev-diff-error': !this.current_ai_ev_data?.valid_map?.[ev_item.ev_id]?.is_satisfied && ev_item.manual_satisfied,
                };
            };
        },
        get_ai_ev_item() {
            return (ev_id) => {
                return this.current_ai_ev_data?.valid_map?.[ev_id] ?? {};
            };
        },
        loading(){
            return this.aiEvDataFetchLoading || this.apply_loading || this.fetch_loading;
        },
        no_ai_audit_title(){
            return (ev_id) => {
                if(this.no_ai_audit)
                    return this.$t('AI 평가 데이터가 없습니다.');
                const current_status = this.current_ai_ev_data?.status ?? null;
                if(current_status === 'completed' && this.get_ai_ev_item(ev_id).converted_reason == null)
                    return this.$t('AI 평가 내용이 없습니다.');
                return aiAuditLoadingText(current_status, this.$i18n);

            };
            
        },
        no_ai_audit_desc(){
            return (ev_id) => {
                if(this.no_ai_audit)
                    return this.$t('상단의 "AI 평가 요청" 버튼을 클릭하여 AI 평가를 요청해주세요.');
                const current_status = this.current_ai_ev_data?.status ?? null;
                if(current_status === 'requested' || current_status === 'processing' || current_status === 'awaiting_ai' || current_status === 'retrying')
                    return `${this.$t('AI 평가가 완료될 때까지 기다려주세요.')}\n${this.$t('평가요청 일시')}: ${this.get_date(this.current_ai_ev_data?.created_at)}`;
                if(current_status === 'failed' || current_status === 'cancelled')
                    return this.$t('상단의 "AI 평가 요청" 버튼을 클릭하여 AI 평가를 다시 요청해주세요.');
                if(this.get_ai_ev_item(ev_id).converted_reason == null)
                    return this.$t('추가적인 내용이 필요하면 상단의 "AI 평가 요청" 버튼을 클릭하여 AI 평가를 다시 요청해주세요.');
                return null;
            };
        }
    },
    watch: {
        arr_ai_ev_data: {
            deep: true,
            handler(){
                this.current_ai_ev_version = this.arr_ai_ev_data?.[0]?.id ?? null;
            }
        }
    },
    mounted() {
        this.current_ai_ev_version = this.arr_ai_ev_data?.[0]?.id ?? null;
    },
    methods: {
        ...mapActions('ai_assessment', ['fetchAiEvData', 'setSubAiEvData']),
        async fetchSubAiEvData() {
            try {
                this.fetch_loading = true;
                
                const aiEvData = await this.fetchAiEvData({sub_id: this.sub_id, loadingControl: false });
                await this.setSubAiEvData({
                    sub_id: this.sub_id,
                    aiEvData: aiEvData,
                });
            }
            catch (e) {
                
            }
            finally {
                this.fetch_loading = false;
            }
          
        },
        async requestAIAssessment() {
            if(this.sub_id == null){
                const h = this.$createElement;
                this.$showModal({
                    type: 'alert',
                    state: 'warning',
                    title: this.$t('AI 평가 요청 실패'),
                    message: h('div', {}, [
                        h('div', {}, this.$t('평가 데이터가 없어 AI 평가를 요청할 수 없습니다.')),
                        h('div', {}, this.$t('먼저 문항에 답변해주세요.')),
                    ]),
                });
                return;
            }
            
            try {
                this.fetch_loading = true;
                const {cpn, cmp} = this.$route.query;
                const response = await this.$axios.post('/assessments/ai-audit', {
                    company_id: cpn, campaign_id: cmp, sub_code: this.sub_code,
                });

                await this.fetchSubAiEvData();
              
                if (response.status === 201) {
                    this.$EventBus.$emit('toast-success', 'AI 평가 요청 완료', response.data.message);
                    return;
                }
                if (response.status === 202) {
                    this.$EventBus.$emit('toast-failure', 'AI 평가 요청 실패', response.data.message);
                }
            }
            catch (error) {
                console.error('AI 평가 요청 중 오류 발생:', error);
                this.$EventBus.$emit('toast-failure', 'AI 평가 요청 실패', 'AI 평가 요청 중 오류가 발생했습니다. 개발팀에 문의해주세요.');
            }
            finally {
                this.fetch_loading = false;
            }
            
        },
        // AI 평가 적용은 판정(충족 여부·카테고리)만 옮긴다. AI 근거·총평은 평가자 코멘트
        // (결과보고서에 실림)에 넣지 않는다 — 운영 요청 2026-10-01.
        copyItem(item) {
            const {converted_reason, is_satisfied} = this.get_ai_ev_item(item.ev_id);
            item.manual_satisfied = is_satisfied;
            item.audit_category_code = converted_reason.audit_category_code;
            this.$emit('handleChange', this.sub_code);
            this.$EventBus.$emit('toast-success', this.$t('AI 평가 적용'), this.$t('단일 항목의 AI 평가를 적용했습니다.'));
        },
        copySub() {
            this.ev_scoring_list.forEach(item => {
                const {converted_reason, is_satisfied} = this.get_ai_ev_item(item.ev_id);
                item.manual_satisfied = is_satisfied;
                item.audit_category_code = converted_reason.audit_category_code;
            });
            this.$emit('handleChange', this.sub_code);
            this.$EventBus.$emit('toast-success', this.$t('AI 평가 적용'), this.$t('문항의 AI 평가를 적용했습니다.'));
        },
        openDetailAIModal() {
            /**
             * api data 정해지면 eventbus로 모달 오픈하는 방향으로 수정
             * @param data
             */
            const {cpn, cmp, cpnm, pc} = this.$route.query;
            this.$EventBus.$emit('ai-assessment-detail-modal:open', {
                project_code: pc,
                company_id: cpn,
                campaign_id: cmp,
                company_name: cpnm,
                vendor_code: null,
                sub_id: this.sub_id,
                label: this.sub_label,
                sub_code: this.sub_code,
                current_ai_ev_version: this.current_ai_ev_version,
            });
        },
        copyAuditBasis(text) {
            if (text) {
                navigator.clipboard.writeText(text).then(() => {
                    this.$EventBus.$emit('single-toast-on', {
                        title: this.$t('AI 평가 근거 복사'),
                        content: this.$t('AI 평가 근거을 클립보드에 복사했습니다.'),
                        variant: 'success',
                    });
                }).catch(() => {
                    this.$EventBus.$emit('single-toast-on', {
                        title: this.$t('복사 실패'),
                        content: this.$t('AI 평가 근거 복사에 실패했습니다.'),
                        variant: 'error',
                    });
                });
            } else {
                this.$EventBus.$emit('single-toast-on', {
                    title: this.$t('복사 실패'),
                    content: this.$t('AI 평가 근거가 없습니다.'),
                    variant: 'warning',
                });
            }
        },
    },
};
</script>

<style lang="scss" scoped>

.audit-desc {
    width: 100%;
    font-size: 0.95rem;
    color: #6c757d;
    margin-bottom: 5px;
}

.audit-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 12px;
    margin-top: 10px;
    margin-right: 12px;

    .audit-category {
    }

    .audit-comment {
        resize: none;
    }
}

.ai-ev-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    font-size: 0.95rem;
    color: #6c757d;

    .versions {
        display: flex;
        align-items: center;
        gap: 8px;

        .version-select {
            width: 100px;
        }
    }
}
.no-ai-audit-data-container{
    margin-top: 9px;
    width: 100%;
    padding: 0 12px;
    margin-bottom: 2px;
    .no-ai-audit_title-wrapper {
        display: flex;
        align-items: center;
        font-size: 1rem;
        color: #6c757d;
        width: 100%;
        .no-ai-audit_title {
            border: 1px solid #ced4da;
            padding: 4px 8px;
            border-radius: 4px;
            width: 100%;
            
            font-weight: bold;
            margin-left: 0;
        }
    }
    .no-ai-audit_desc {
        margin-left: 24px;
        border: 1px solid #ced4da;
        padding: 8px;
        height: 142px;
        border-radius: 4px;
        font-size: 0.95rem;
        color: #6c757d;
        margin-top: 12px;
        white-space: pre-wrap;
    }
}

.ai-report-icon {
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
        color: #007bff !important;
        transform: scale(1.1);
    }
}
.ai-ev-container{
    margin-top: -41px;
}
</style>
